import { useRef, useCallback } from "react";
import { Loader, PostCard } from "@/components/shared";
import { useGetRecentPosts } from "@/lib/react-query/queries";
import SEOComponent from "@/components/shared/SEOComponent";
import { useOutletContext } from "react-router-dom";

interface OutletContextType {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}

const Dealers = () => {
  const { selectedCategory } = useOutletContext<OutletContextType>();

  // ✅ FIXED: Pass all 3 parameters correctly
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetRecentPosts(
    selectedCategory,  // category
    "sell-something",  // individual  
    true              // isActivated
  );

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastPostRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  if (isError) {
    return (
      <div className="flex flex-1">
        <div className="home-container">
          <p className="body-medium text-light-1">Something went wrong</p>
        </div>
      </div>
    );
  }

  // ✅ No need to filter again - the query already filters
  const allPosts = data?.pages?.flatMap((page) => page.documents) ?? [];

  const showNoResults = !isLoading && allPosts.length === 0;
  const showEndMessage = !hasNextPage && !isLoading && allPosts.length > 0;

  return (
    <>
     <SEOComponent
        title="Dealers Feed | Initiate Secure Business Deals on Busiconn"
        description="Browse verified deal posts from dealers offering services, digital work, and goods. Initiate business conversations and close deals securely with escrow protection, verification, and dispute resolution on Busiconn."
        canonicalUrl="https://busiconn.com/dealers"
        keywords="business dealers platform, secure deal initiation, online business deals, escrow protected transactions, service providers deals, freelance deal platform, B2B deal feed, trusted online dealers"
        ogType="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Dealers Feed",
          description:
            "A feed of verified deal posts from dealers initiating secure online business deals on Busiconn.",
          url: "https://busiconn.com/dealers",
          isPartOf: {
            "@type": "WebSite",
            name: "Busiconn",
            url: "https://busiconn.com",
          },
          mainEntity: {
            "@type": "ItemList",
            name: "Deal Posts",
            description:
              "List of deal posts created by dealers to initiate escrow-protected business transactions.",
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://busiconn.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Dealers",
                item: "https://busiconn.com/dealers",
              },
            ],
          },
        }}
      />

      
      <div className="flex flex-1">
        <div className="home-container">
          <h2 className="h3-bold md:h2-bold text-left w-full">Dealers Feed</h2>

          {/* Loading State */}
          {isLoading && !data ? (
            <Loader />
          ) : (
            <>
              {/* Posts List */}
              <ul className="flex flex-col flex-1 gap-6 w-full">
                {allPosts.map((post, index) => (
                  <li
                    key={post.$id}
                    ref={index === allPosts.length - 1 ? lastPostRef : null}
                    className="flex justify-center w-full"
                  >
                    <PostCard post={post} showDealBadge={true} />
                  </li>
                ))}
              </ul>

              {/* Loading More Indicator */}
              {isFetchingNextPage && <Loader />}

              {/* No Results */}
              {showNoResults && (
                <div className="flex-center flex-col gap-4 mt-10">
                  <img 
                    src="/assets/icons/no-deals.svg" 
                    alt="No Dealers"
                    className="h-24 w-24"
                  />
                  <p className="text-light-3 text-center">
                    No dealer posts available yet
                  </p>
                </div>
              )}

              {/* End of Results */}
              {showEndMessage && (
                <p className="text-center text-light-3 mt-4">You've reached the end</p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Dealers;